import { Button, Checkbox, Group, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useState } from "react";
import { useParams } from "react-router";
import type { ApiResponse } from "../../../../../api";
import api from "../../../../../api";
import { useSiteCategories } from "../../../../../hooks/useSiteCategories";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../../../mantine/notifications/useToast";
import urls from "../../../../../urls";
import type { Todo, TodoFile } from "../../../../../utils/backend-types";
import { downloadCsvOrExcelFileForResponseWithContentDisposition } from "../../../../../utils/files/downloadCsvOrExcelFileForResponseWithContentDisposition";
import { MultiConfirmationModal } from "../../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { useShouldShowStaffView } from "../../../../StaffViewToggle/useShouldShowStaffView";
import { useRevokeTodoDocumentMutation } from "../../../hooks/useRevokeTodoDocumentMutation";
import { useTodoCanGenerateDocuments } from "../../../hooks/useTodoCanGenerateDocuments";
import { SYSTEM_USER_NAME } from "../../../Todos.constants";
import { Mode } from "../../../Todos.types";
import { getColumns } from "./columns/getColumns";
import { FormAdminButton } from "./FormAdminButton/FormAdminButton";
import { RevokeDocumentInstruction } from "./RevokeDocumentInstruction/RevokeDocumentInstruction";

export interface DocumentsListProps {
  documents: Array<TodoFile>;
  todoId: number;
  storeTodo: (todo: Todo) => void;
  mode: Mode;
  documentProvided?: boolean;
  showTodoStatus?: boolean;
  isRevokeable?: boolean;
  variantId?: number;
  onClickRename: (document: TodoFile, newName: string) => void;
  onClickDelete: (document: TodoFile) => void;
  deleteDocuments: (ids: Array<number>) => Promise<ApiResponse>;
  updateDocumentProvided: (todoId: number) => void;
  onClickNotify: (todoId: number) => Promise<ApiResponse>;
  handleErrorMessage: (message: string) => void;
}

function DocumentsList({
  documents,
  todoId,
  storeTodo,
  mode,
  documentProvided,
  showTodoStatus,
  isRevokeable = false,
  variantId,
  onClickRename,
  onClickDelete,
  deleteDocuments,
  updateDocumentProvided,
  onClickNotify,
  handleErrorMessage
}: DocumentsListProps) {
  const { showToast, showErrorToast } = useToast();
  const { projectId } = useParams();
  const { siteCategories } = useSiteCategories(projectId || null);

  const { revokeTodoDocument } = useRevokeTodoDocumentMutation({
    mode,
    variantId
  });

  const { todoCanGenerateDocuments } = useTodoCanGenerateDocuments(
    todoId,
    handleErrorMessage
  );

  const { is_form_admin_user } = todoCanGenerateDocuments ?? {};
  const isPartialFeedin =
    siteCategories?.some((site) => !site.is_full_feedin) || false;

  const [documentsToDelete, setDocumentsToDelete] = useState<Array<TodoFile>>(
    []
  );
  const [documentsToRevoke, setDocumentsToRevoke] = useState<Array<TodoFile>>(
    []
  );
  const [isNotifying, setIsNotifying] = useState<boolean>(false);

  const { shouldShowStaffView } = useShouldShowStaffView();
  const canAlwaysRenameAndDelete = mode !== Mode.Normal || shouldShowStaffView;

  const toggleDeleteDocumentsModal = () => setDocumentsToDelete([]);

  function handleClickDownloadSelection(selectedDocuments: Array<TodoFile>) {
    const selectedDocumentIds = selectedDocuments.map(
      (document) => document.id
    );

    api
      .post(urls.api.todoDownloadSelectedDocuments(todoId), {
        todoFileIds: selectedDocumentIds
      })
      .then((response) =>
        downloadCsvOrExcelFileForResponseWithContentDisposition(response, true)
      )
      .catch((error) => showErrorToast(error));
  }

  function handleClickNotify() {
    setIsNotifying(true);
    onClickNotify(todoId)
      .then(() => {
        showToast({
          type: "success",
          message: "Die Benachrichtigung wurde versandt!"
        });
      })
      .finally(() => setIsNotifying(false));
  }

  function handleClickDeleteSelection(selectedDocuments: Array<TodoFile>) {
    setDocumentsToDelete(selectedDocuments);
  }
  function handleClickRevokeSelection(selectedDocuments: Array<TodoFile>) {
    setDocumentsToRevoke(
      selectedDocuments.filter((document) => document.revoked === null)
    );
  }

  function handleDeleteDocuments(ids: Array<number>) {
    return deleteDocuments(ids)
      .then(() => {
        mrtTable.setRowSelection({});
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        setDocumentsToDelete([]);
      });
  }

  function handleRevokeDocument(documentId: number) {
    return revokeTodoDocument({ todoId, documentId })
      .then(() => {
        mrtTable.setRowSelection({});
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        setDocumentsToRevoke([]);
      });
  }

  const notifyButtonDisabled = documentProvided === false;
  const documentsToDeleteModalIsOpen = documentsToDelete.length > 0;
  const documentsToDeleteNames = documentsToDelete.map(
    (document) => document.name
  );
  const documentsToDeleteIds = documentsToDelete.map((document) => document.id);

  const mrtTable = useMantineReactTable<TodoFile>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Keine Dokumente vorhanden",
      initialState: { sorting: [{ id: "creationDatetime", desc: true }] },
      hideBorder: true
    }),
    ...getMRTTopToolbarCustomActionsRenderer({
      create: { hide: true },
      delete: { hide: true }
    }),
    enableToolbarInternalActions: false,
    renderTopToolbarCustomActions: ({ table }) => (
      <Group justify="flex-end" p={0} w="auto">
        {isRevokeable && (
          <Button
            color="primary"
            disabled={
              !table
                .getSelectedRowModel()
                .rows.some((document) => document.original.revoked === null)
            }
            onClick={() =>
              handleClickRevokeSelection(
                table.getSelectedRowModel().rows.map((row) => row.original)
              )
            }
          >
            Ungültig markieren
          </Button>
        )}
        {canAlwaysRenameAndDelete && (
          <Button
            color="primary"
            disabled={table.getSelectedRowModel().rows.length === 0}
            onClick={() =>
              handleClickDeleteSelection(
                table.getSelectedRowModel().rows.map((row) => row.original)
              )
            }
          >
            {dataText} löschen
          </Button>
        )}
        <Button
          color="primary"
          disabled={table.getSelectedRowModel().rows.length === 0}
          onClick={() =>
            handleClickDownloadSelection(
              table.getSelectedRowModel().rows.map((row) => row.original)
            )
          }
        >
          {dataText} herunterladen
        </Button>
      </Group>
    ),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        other: isRevokeable
          ? [
              {
                text: "Dokument ungültig markieren",
                icon: IconX,
                enableAction: (cell) => cell.row.original.revoked === null,
                onClick: (row) => setDocumentsToRevoke([row.original])
              }
            ]
          : [],
        edit: {
          text: "Dokument umbenennen",
          enableEdit: (cell) =>
            canAlwaysRenameAndDelete ||
            cell.row.original.createdBy?.name !== SYSTEM_USER_NAME
        },
        delete: {
          text: "Dokument löschen",
          enableDelete: (cell) =>
            canAlwaysRenameAndDelete ||
            cell.row.original.createdBy?.name !== SYSTEM_USER_NAME,
          onClick: (row) => onClickDelete(row.original)
        }
      },
      displayColumnDefOptions: {
        "mrt-row-actions": {
          size: 80
        },
        "mrt-row-select": {
          size: 0
        }
      }
    }),
    onEditingRowSave: ({ exitEditingMode, row, values }) => {
      onClickRename(row.original, values.name);
      exitEditingMode();
    },
    enableEditing: (row) => !row.original.revoked,
    layoutMode: "grid",
    enablePagination: false,
    enableBottomToolbar: false,
    columns: getColumns(),
    data: documents
  });

  const dataText =
    mrtTable.getSelectedRowModel().rows.length <= 1 ? "Datei" : "Dateien";

  return (
    <div className="DocumentsList">
      {isRevokeable && <RevokeDocumentInstruction />}
      <Group
        className="controls"
        justify={
          is_form_admin_user || (showTodoStatus && isPartialFeedin)
            ? "space-between"
            : "flex-end"
        }
        mb="sm"
      >
        {showTodoStatus && isPartialFeedin && (
          <Group gap="xs">
            {shouldShowStaffView && (
              <div className="document-provided-checkbox">
                <Checkbox
                  checked={documentProvided}
                  onChange={() => updateDocumentProvided(todoId)}
                ></Checkbox>
              </div>
            )}
            {(shouldShowStaffView || documentProvided) && (
              <Text>
                {!documentProvided && shouldShowStaffView
                  ? "Als bereitgestellt markieren"
                  : "Dokument wurde bereitgestellt"}
              </Text>
            )}
            {shouldShowStaffView && (
              <Button
                color="primary"
                disabled={notifyButtonDisabled}
                loading={isNotifying}
                onClick={() => handleClickNotify()}
              >
                Benachrichtigen
              </Button>
            )}
          </Group>
        )}
        {is_form_admin_user && (
          <FormAdminButton todoId={todoId} onUpdateTodo={storeTodo} />
        )}
      </Group>
      <MantineReactTable table={mrtTable} />
      <MultiConfirmationModal
        actionName="löschen"
        actionObjects={documentsToDeleteNames}
        confirmationText="Möchten Sie die folgenden Dokumente löschen? Dieser Schritt kann nicht rückgängig gemacht werden."
        isModalOpen={documentsToDeleteModalIsOpen}
        objectName="Dokumente"
        toggleModal={toggleDeleteDocumentsModal}
        onAction={() => handleDeleteDocuments(documentsToDeleteIds)}
      />
      <MultiConfirmationModal
        actionName="ungültig markieren"
        actionObjects={documentsToRevoke.map((document) => document.name)}
        confirmationText={`Möchten Sie ${documentsToRevoke.length === 1 ? "das" : "die"} Dokument${documentsToRevoke.length === 1 ? " des" : "e der"} als ungültig markieren? Dieser Schritt kann nicht rückgängig gemacht werden.`}
        isModalOpen={documentsToRevoke.length > 0}
        objectName="Dokument"
        toggleModal={() => setDocumentsToRevoke([])}
        onAction={async () => {
          await Promise.all(
            documentsToRevoke.map((document) =>
              handleRevokeDocument(document.id)
            )
          );

          setDocumentsToRevoke([]);
        }}
      />
    </div>
  );
}

export { DocumentsList };
